import React, { useState } from "react";
import { Upload, Button, message, Modal, List, Typography } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { CALL_API } from "common/API";

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Header normalization
// The spreadsheet's real headers are:
//   NO, Name, Work Email, Personal email, Joining Date, Gender,
//   Location Name, Department Name, "Designation/job tiitle ",
//   Category Name, Date Of Birth, KRA PIN, ID No, NSSF No.
// This strips spacing/punctuation/case so header changes (extra spaces,
// "tiitle" vs "title", "NSSF No." vs "NSSF No") don't silently break parsing.
// ---------------------------------------------------------------------------
const normalizeKey = (key = "") =>
  key
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const COLUMN_ALIASES = {
  name: ["name", "fullname", "employeename"],
  workEmail: ["workemail", "email", "companyemail", "officialemail"],
  personalEmail: ["personalemail"],
  joiningDate: ["joiningdate", "startdate", "dateofjoining", "hireddate"],
  gender: ["gender", "sex"],
  locationName: ["locationname", "location", "branch"],
  departmentName: ["departmentname", "department"],
  designation: [
    "designationjobtiitle", // matches the sheet's actual (typo'd) header
    "designationjobtitle",
    "designation",
    "jobtitle",
  ],
  categoryName: ["categoryname", "category", "level"],
  dateOfBirth: ["dateofbirth", "dob"],
  kraPin: ["krapin", "pin"],
  idNo: ["idno", "nationalid", "nationalidno"],
  nssfNo: ["nssfno", "nssf"],
};

const normalizeRow = (row) => {
  const normalized = {};
  Object.keys(row).forEach((key) => {
    const value = row[key];
    normalized[normalizeKey(key)] =
      typeof value === "string" ? value.trim() : value;
  });

  const result = {};
  Object.entries(COLUMN_ALIASES).forEach(([field, aliases]) => {
    const matchKey = aliases.find((alias) => normalized[alias] !== undefined);
    result[field] = matchKey ? normalized[matchKey] : "";
  });
  return result;
};

// ---------------------------------------------------------------------------
// Role resolution
// "Category Name" in the sheet IS the role name (matches roleList.roleName
// directly) — e.g. "Overall Head", "Head of Department", "employee", etc.
// No keyword guessing based on job title; just a direct, case/whitespace
// -insensitive lookup against the real roleList passed into this component.
// ---------------------------------------------------------------------------
const getRole = (categoryName = "", roleList = []) => {
  const categoryLc = categoryName.trim().toLowerCase();
  if (!categoryLc) return null;

  return (
    roleList.find(
      (role) => role.roleName?.trim().toLowerCase() === categoryLc
    ) || null
  );
};

const getDepartment = (departmentName, departmentList = []) =>
  departmentList.find(
    (item) =>
      item.department_name?.trim().toLowerCase() ===
      departmentName?.trim().toLowerCase()
  );

const getLocation = (locationName, locationList = []) =>
  locationList.find(
    (item) =>
      item.location_name?.trim().toLowerCase() ===
      locationName?.trim().toLowerCase()
  );

// Splits a full name into first/last name. Anything after the first word
// becomes the last name, matching how `full_name` is rebuilt for the API.
const splitName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const firstname = parts[0] || "";
  const lastname = parts.slice(1).join(" ") || "";
  return { firstname, lastname };
};

const formatDate = (value) => {
  if (!value && value !== 0) return "";

  try {
    // Excel serial date number
    if (typeof value === "number") {
      const date = XLSX.SSF.parse_date_code(value);
      return `${date.y}-${String(date.m).padStart(2, "0")}-${String(
        date.d
      ).padStart(2, "0")}`;
    }

    // Already a JS Date object (sheet_to_json with cellDates: true)
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return "";
      return value.toISOString().split("T")[0];
    }

    if (typeof value === "string") {
      const trimmed = value.trim();

      // Handles rows where the date cell is stored as TEXT rather than a
      // real date, e.g. "17/11/2025" (D/M/YYYY). new Date() on this string
      // is unreliable — JS often tries MM/DD/YYYY and returns Invalid Date
      // whenever the "day" part is > 12, silently dropping the date.
      const dmy = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (dmy) {
        const [, d, m, y] = dmy;
        const day = Number(d);
        const month = Number(m);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          return `${y}-${String(month).padStart(2, "0")}-${String(
            day
          ).padStart(2, "0")}`;
        }
      }

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) return parsed.toISOString().split("T")[0];
    }

    return "";
  } catch {
    return "";
  }
};

// Sheet has "M" / "F" (sometimes "Male" / "Female"); the Add Employee form
// only accepts the full lowercase words "male" / "female", so this maps
// either form to what the backend expects.
const normalizeGender = (value = "") => {
  const v = value.trim().toLowerCase();
  if (v === "m" || v === "male") return "male";
  if (v === "f" || v === "female") return "female";
  return "";
};

// Guards against the "String(undefined) === 'undefined'" bug: only
// stringifies real values, otherwise returns "".
const safeString = (value) =>
  value !== undefined && value !== null && value !== "" ? String(value) : "";

const BulkEmployeeUpload = ({
  ClientId,
  roleList = [],
  departmentList = [],
  locationList = [],
}) => {
  const [uploading, setUploading] = useState(false);
  const [resultModal, setResultModal] = useState(null);

  const handleFile = async (file) => {
    try {
      setUploading(true);
      const data = await file.arrayBuffer();

      const workbook = XLSX.read(data, { cellDates: true });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      await uploadEmployees(rawRows.map(normalizeRow));
    } catch (error) {
      console.error(error);
      message.error("Failed to read Excel file");
    } finally {
      setUploading(false);
    }

    return false; // prevent antd's default upload behaviour
  };

  const uploadEmployees = async (employees) => {
    let successCount = 0;
    const failedEmployees = [];
    const unmatchedRole = [];

    for (const emp of employees) {
      const { firstname, lastname } = splitName(emp.name);
      const displayName = emp.name || emp.workEmail || "Unnamed row";

      if (!emp.name || !emp.workEmail) {
        failedEmployees.push(`${displayName} (missing Name or Work Email)`);
        continue;
      }

      try {
        const role = getRole(emp.categoryName, roleList);
        const department = getDepartment(emp.departmentName, departmentList);
        const location = getLocation(emp.locationName, locationList);

        if (!role) unmatchedRole.push(displayName);

        const dob = formatDate(emp.dateOfBirth);
        const dobDate = dob ? new Date(dob) : null;
        const age =
          dobDate && !isNaN(dobDate.getTime())
            ? new Date().getFullYear() - dobDate.getFullYear()
            : 0;

        const userId = emp.workEmail.toLowerCase().trim();

        const payload = {
          leaveAccurals: [],

          user_id: userId,
          full_name: `${firstname} ${lastname}`.trim(),
          password: "12345678",

          start_date: formatDate(emp.joiningDate),
          dateofbirth: dob,

          gender: normalizeGender(emp.gender),
          age,
          maritalstatus: "",
          nationality: "",

          role_id: role?.roleId ? [role.roleId] : [],
          role_name: role?.roleName ? [role.roleName] : [],

          bandname: "",
          bandid: "",

          employementtype: "",

          institutename: "",
          degree: "",
          specialization: "",
          yearofcompletion: "",

          companyname: "",
          jobtitle: emp.designation || "",

          professionalsince: "",
          hireddate: formatDate(emp.joiningDate),

          relevant: "",
          employeesponser: "",

          statusname: "active",
          status_id: "1",

          jobdescription: emp.categoryName || "",

          staff_department_id: department?.department_id || "",

          overtime_status: "",
          salary: "",

          address: "",
          phoneno: "",

          profile_url: null,

          presentaddress: "",
          permanentaddress: "",

          emergencycontactname: "",
          emergencycontactnumber: "",

          baselocationid: location?.location_id || "",
          basedepartmentid: department?.department_id || "",

          usertimezone: "",

          salutation: "",
          initial: "",
          firstname,
          lastname,

          nssf: safeString(emp.nssfNo),
          national_id: safeString(emp.idNo),
          krapin: safeString(emp.kraPin),
          sharegnum: "",
          driverslicense: "",
          contract: "",

          reportingmanager: "",

          lastpromotionlevel: "",
          lastpromotiondate: "",

          probationstartdate: "",
          probationenddate: "",

          contractstartdate: "",
          contractenddate: "",

          educationdetails: [],
          professionalcertifications: [],
          workexperience: [],
          practicaltrainings: [],

          onboardingemployee: "",
        };

        const response = await CALL_API(`add-users/${ClientId}`, "post", payload);

        if (response?.code === "SUCCESS") {
          successCount++;
        } else {
          failedEmployees.push(
            `${displayName}${response?.message ? ` (${response.message})` : ""}`
          );
        }
      } catch (error) {
        console.error(`Employee Upload Failed: ${displayName}`, error);
        // Surface whatever the backend/network layer actually said, so the
        // modal shows a real reason instead of just the person's name.
        const reason =
          error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "Unknown error — check the browser console/network tab";
        failedEmployees.push(`${displayName} (${reason})`);
      }
    }

    setResultModal({ successCount, failedEmployees, unmatchedRole });

    if (failedEmployees.length > 0) {
      message.warning(
        `${successCount} employees uploaded successfully. ${failedEmployees.length} failed.`
      );
    } else {
      message.success(`${successCount} employees uploaded successfully`);
    }

    if (unmatchedRole.length > 0) {
      message.info(
        `${unmatchedRole.length} employee(s) uploaded without a matched role — assign manually.`
      );
    }
  };

  return (
    <>
      <Upload
        beforeUpload={handleFile}
        accept=".xlsx,.xls"
        showUploadList={false}
        maxCount={1}
        disabled={uploading}
      >
        <Button icon={<UploadOutlined />} loading={uploading}>
          Bulk Employee Upload
        </Button>
      </Upload>

      <Modal
        title="Bulk Upload Results"
        open={!!resultModal}
        onCancel={() => setResultModal(null)}
        onOk={() => setResultModal(null)}
        footer={null}
      >
        {resultModal && (
          <>
            <Text strong>{resultModal.successCount} employee(s) uploaded successfully.</Text>

            {resultModal.failedEmployees.length > 0 && (
              <>
                <p style={{ marginTop: 16, marginBottom: 4 }}>
                  <Text type="danger" strong>
                    Failed ({resultModal.failedEmployees.length}):
                  </Text>
                </p>
                <List
                  size="small"
                  dataSource={resultModal.failedEmployees}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              </>
            )}

            {resultModal.unmatchedRole.length > 0 && (
              <>
                <p style={{ marginTop: 16, marginBottom: 4 }}>
                  <Text type="warning" strong>
                    Uploaded without a matched role ({resultModal.unmatchedRole.length}) —
                    please assign manually:
                  </Text>
                </p>
                <List
                  size="small"
                  dataSource={resultModal.unmatchedRole}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default BulkEmployeeUpload;